import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  InputAdornment
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TransportationOption } from '@/types';
import { useI18n } from '@/hooks/useI18n';

const transportationOptionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['individual', 'group', 'public']),
  pricePerPerson: z.number().min(0, 'Price must be non-negative')
});

type TransportationOptionFormData = z.infer<typeof transportationOptionSchema>;

interface EditTransportationOptionFormProps {
  transportationOption: TransportationOption;
  onUpdateTransportationOption: (option: TransportationOption) => void;
  trigger: React.ReactElement;
}

const EditTransportationOptionForm: React.FC<EditTransportationOptionFormProps> = ({
  transportationOption,
  onUpdateTransportationOption,
  trigger
}) => {
  const [open, setOpen] = React.useState(false);
  const { t } = useI18n();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<TransportationOptionFormData>({
    resolver: zodResolver(transportationOptionSchema),
    defaultValues: {
      name: transportationOption.name,
      type: transportationOption.type as 'individual' | 'group' | 'public',
      pricePerPerson: transportationOption.pricePerPerson
    }
  });

  React.useEffect(() => {
    reset({
      name: transportationOption.name,
      type: transportationOption.type as 'individual' | 'group' | 'public',
      pricePerPerson: transportationOption.pricePerPerson
    });
  }, [transportationOption, reset]);

  const onSubmit = (data: TransportationOptionFormData) => {
    onUpdateTransportationOption({
      ...transportationOption,
      ...data
    });
    setOpen(false);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  return (
    <>
      {React.cloneElement(trigger, {
        onClick: () => setOpen(true)
      })}
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('transportation.form.editTitle')}
        </DialogTitle>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('transportation.form.name')}
                    placeholder={t('transportation.form.namePlaceholder')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                    required
                  />
                )}
              />

              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>{t('transportation.form.type')}</InputLabel>
                    <Select
                      {...field}
                      label={t('transportation.form.type')}
                    >
                      <MenuItem value="individual">
                        🚗 {t('transportation.types.individual')}
                      </MenuItem>
                      <MenuItem value="group">
                        🚌 {t('transportation.types.group')}
                      </MenuItem>
                      <MenuItem value="public">
                        🚆 {t('transportation.types.public')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="pricePerPerson"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('transportation.form.pricePerPerson')}
                    placeholder={t('transportation.form.pricePerPersonPlaceholder')}
                    value={value}
                    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                    error={!!errors.pricePerPerson}
                    helperText={errors.pricePerPerson?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {t('transportation.form.updateTransportationOption')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default EditTransportationOptionForm;